<template>
  <HeaderComponent />
  <div class="fixed top-[80px] left-[50%] z-[100] translate-x-[-50%] w-[80%] max-w-[400px] flex flex-col">
    <div v-if="errorMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-red-700 my-[10px] font-bold text-center p-[20px]">{{ errorMessage }}</div>
    <div v-if="successMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-green-700 my-[10px] font-bold text-center p-[20px]">{{ successMessage }}</div>
  </div>

  <section class="w-full min-h-screen pt-[120px] pb-[60px] bg-gray-100 flex justify-center items-start">
    <div class="container lg:max-w-[1200px] px-[20px] flex justify-center w-full">
      <div class="w-full max-w-[420px] bg-white rounded-[10px] shadow-4xl overflow-hidden">
        <div class="w-full bg-orange-600 p-[25px] flex flex-col items-center">
          <img src="../assets/logo.png" alt="" class="w-[50px] h-[50px] mb-[10px]">
          <h1 class="text-white text-xl font-bold">Mot de passe oublié</h1>
          <p class="text-orange-100 text-xs mt-[5px]">{{ step === 'demande' ? 'Recevez un code par email' : 'Saisissez le code reçu' }}</p>
        </div>

        <!-- Étape 1 : demande du code -->
        <form v-if="step === 'demande'" @submit.prevent="requestCode" class="w-full p-[25px]">
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Numéro WhatsApp</label>
            <input v-model="whatsapp" type="tel" required class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Le numéro de votre compte">
          </div>
          <p class="text-xs text-gray-400">Un code de vérification sera envoyé à l'email associé à ce compte, s'il y en a un.</p>
          <button type="submit" :disabled="isLoading" class="w-full h-[48px] mt-[15px] flex justify-center items-center p-[10px] text-sm font-bold rounded-[5px] bg-orange-600 hover:bg-orange-700 text-gray-100 transition-colors">
            <span v-if="!isLoading">Recevoir le code</span>
            <div v-else class="spinner1"><div></div><div></div><div></div><div></div><div></div></div>
          </button>
          <p class="text-center text-sm text-gray-500 mt-[20px]">
            <router-link to="/connexion" class="text-orange-600 font-bold hover:underline">Retour à la connexion</router-link>
          </p>
        </form>

        <!-- Étape 2 : code + nouveau mot de passe -->
        <form v-else @submit.prevent="resetPassword" class="w-full p-[25px]">
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Code reçu par email</label>
            <input v-model="code" type="text" inputmode="numeric" maxlength="6" required class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500 tracking-[4px]" placeholder="000000">
          </div>
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Nouveau mot de passe</label>
            <div class="relative">
              <input v-model="newPassword" :type="showPassword ? 'text' : 'password'" required class="w-full p-[12px] pr-[42px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Nouveau mot de passe">
              <button type="button" @click="showPassword = !showPassword" class="absolute right-[12px] top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <i :class="showPassword ? 'fa-eye-slash' : 'fa-eye'" class="fa-solid"></i>
              </button>
            </div>
          </div>
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Confirmer le mot de passe</label>
            <div class="relative">
              <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" required class="w-full p-[12px] pr-[42px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Retapez le mot de passe">
              <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute right-[12px] top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <i :class="showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'" class="fa-solid"></i>
              </button>
            </div>
            <p v-if="confirmPassword && newPassword !== confirmPassword" class="text-xs text-red-500 mt-[5px]">Les mots de passe ne correspondent pas.</p>
          </div>
          <button type="submit" :disabled="isLoading" class="w-full h-[48px] mt-[15px] flex justify-center items-center p-[10px] text-sm font-bold rounded-[5px] bg-orange-600 hover:bg-orange-700 text-gray-100 transition-colors">
            <span v-if="!isLoading">Réinitialiser le mot de passe</span>
            <div v-else class="spinner1"><div></div><div></div><div></div><div></div><div></div></div>
          </button>
          <p class="text-center text-sm text-gray-500 mt-[20px]">
            <button type="button" @click="step = 'demande'" class="text-orange-600 font-bold hover:underline">Renvoyer un code</button>
          </p>
        </form>
      </div>
    </div>
  </section>
  <FooterComponent />
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { useAuthClientStore } from '@/store/useAuthClientStore'

const router = useRouter()
const route = useRoute()
const authClientStore = useAuthClientStore()

const step = ref('demande')
const whatsapp = ref('')
const code = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const showMsg = (ref_, msg) => { ref_.value = msg; setTimeout(() => (ref_.value = ''), 5000) }

const requestCode = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.post('/api/clients/forgot-password', { whatsapp: whatsapp.value })
    showMsg(successMessage, data.message)
    step.value = 'code'
  } catch (error) {
    showMsg(errorMessage, error.response?.data?.error || 'Erreur lors de la demande.')
  } finally {
    isLoading.value = false
  }
}

const resetPassword = async () => {
  if (newPassword.value !== confirmPassword.value) {
    showMsg(errorMessage, 'Les mots de passe ne correspondent pas.')
    return
  }
  isLoading.value = true
  try {
    const { data } = await axios.post('/api/clients/reset-password', {
      whatsapp: whatsapp.value,
      code: code.value,
      newPassword: newPassword.value
    })
    authClientStore.setAuth(data.token, data.client)
    showMsg(successMessage, 'Mot de passe réinitialisé avec succès !')
    const redirect = route.query.redirect || '/mon-espace'
    setTimeout(() => router.push(redirect), 500)
  } catch (error) {
    showMsg(errorMessage, error.response?.data?.error || 'Erreur lors de la réinitialisation.')
  } finally {
    isLoading.value = false
  }
}
</script>
